import "./style.css";

import React, { useEffect, useMemo, useState } from "react";

import monitorApi from "../../api/monitorApi";
import Header from "../../components/Header/Header";
import CheckDomain from "./CheckDomain/CheckDomain";
import { getEffectiveConfig, getInitialColumnFilters } from "./config/config";
import DateSwitcher, {
  formatDateForDisplay,
  getCurrentDateString,
} from "./DateSwitcher/DateSwitcher";
import MonitoringFilterRow from "./MonitoringFilterRow/MonitoringFilterRow";
import {
  DnsRecordType,
  MonitoringDataType,
  MonitoringPagePropsType,
} from "./MonitoringPageTypes";
import MonitoringTableHead from "./MonitoringTableHead/MonitoringTableHead";
import {
  buildDnsTooltip,
  buildSimpleTooltip,
  HeaderHelpIcon,
  HoverTooltip,
} from "./MonitoringTooltip/MonitoringTooltip";
import { useMonitoringTooltip } from "./useMonitoringTooltip/useMonitoringTooltip";

function formatAbsoluteDate(dateString: string) {
  const parsedDate = new Date(dateString);

  if (Number.isNaN(parsedDate.getTime())) {
    return dateString;
  }

  const months = [
    "Января",
    "Февраля",
    "Марта",
    "Апреля",
    "Мая",
    "Июня",
    "Июля",
    "Августа",
    "Сентября",
    "Октября",
    "Ноября",
    "Декабря",
  ];

  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = months[parsedDate.getMonth()];
  const year = parsedDate.getFullYear();
  const hours = String(parsedDate.getHours()).padStart(2, "0");
  const minutes = String(parsedDate.getMinutes()).padStart(2, "0");

  return `${day} ${month}, ${year}. ${hours}:${minutes}`;
}

function formatUpdatedAt(
  dateString: string,
  /* istanbul ignore next */ recentUpdateThresholdHours = 24,
) {
  const parsedDate = new Date(dateString);

  const now = new Date();
  const diffMs = now.getTime() - parsedDate.getTime();

  if (diffMs < 0) {
    return "только что";
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);

  if (Number.isNaN(diffHours) || diffHours >= recentUpdateThresholdHours) {
    return formatAbsoluteDate(dateString);
  }

  if (diffMinutes < 1) {
    return "только что";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} мин назад`;
  }

  return `${diffHours} ч назад`;
}

function getDnsStatus(dnsRecords: DnsRecordType[]) {
  const list = Array.isArray(dnsRecords) ? dnsRecords : [];
  const resolvedCount = list.filter((item) => item.resolved).length;
  const totalCount = list.length;

  if (totalCount === 0 || resolvedCount === 0) {
    const unresolved = list.filter((x) => x && !x.resolved);
    const allNxdomain =
      unresolved.length > 0 &&
      unresolved.every(
        (x) => x.error && String(x.error).toUpperCase().includes("NXDOMAIN"),
      );
    if (allNxdomain) {
      return { label: "NXDOMAIN", variant: "unresolved" };
    }
    return { label: "unresolved", variant: "unresolved" };
  }

  if (resolvedCount === totalCount) {
    return { label: "resolved", variant: "resolved" };
  }

  return { label: `partial`, variant: "partial" };
}

function getNsdiStatus(nsdiValue: string | null | undefined) {
  if (nsdiValue === "resolved")
    return { label: "resolved", variant: "resolved" };
  if (nsdiValue === "no_data") return { label: "no_data", variant: "unknown" };
  // NXDOMAIN, Timeout, or other error: show actual value, style as unresolved
  if (nsdiValue && nsdiValue !== "resolved" && nsdiValue !== "no_data")
    return { label: nsdiValue, variant: "unresolved" };
  return { label: "unresolved", variant: "unresolved" };
}
function toNum(v: any) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function isNotFullyResolved(dnsRecords: DnsRecordType[]) {
  return getDnsStatus(dnsRecords).variant !== "resolved";
}

/* istanbul ignore next */
function loadingDateSwitcherNoop() {}

function HelperTextDns() {
  return (
    <span>
      DNS-провайдер <em>(Domain Name System)</em> сопоставляет доменное имя
      (например, nasvyazi.org) и IP-адрес, чтобы твой компьютер знал, куда
      именно ему идти. Это как справочник адресов: чтобы пойти в гости к другу
      Максу (доменное имя), нужно отправиться по адресу ул. Пушкина, д. 123
      (IP-адрес).
    </span>
  );
}

const MonitoringPage: React.FC<MonitoringPagePropsType> = ({
  config: configOverrides,
}) => {
  const effectiveConfig = getEffectiveConfig(configOverrides);
  const { PAGE_SIZE, RECENT_UPDATE_THRESHOLD_HOURS, columns } = effectiveConfig;
  const initialFilters = getInitialColumnFilters(columns);

  const [monitoringData, setMonitoringData] =
    useState<MonitoringDataType | null>(null);
  const [sessionIndex, setSessionIndex] = useState(0);
  const [sessionSwitchLoading, setSessionSwitchLoading] = useState(false);
  const [columnFilters, setColumnFilters] =
    useState<Record<string, string>>(initialFilters);
  const [page, setPage] = useState(1);

  const {
    tooltip,
    showTooltip,
    moveTooltip,
    hideTooltip,
    tooltipRootRef,
    tooltipScrollRef,
    setScrollHover,
  } = useMonitoringTooltip();

  useEffect(() => {
    monitorApi
      .getDnsMonitoringData()
      .then((data) => {
        setMonitoringData(data);
        setSessionIndex(0);
      })
      .catch(() => {
        setMonitoringData({ error: true } as MonitoringDataType);
      });
  }, []);

  const sessions = Array.isArray(monitoringData?.dns_check_sessions)
    ? monitoringData!.dns_check_sessions
    : [];
  const canGoLeft = sessionIndex < sessions.length - 1;
  const canGoRight = sessionIndex > 0;

  const handleSessionSwitch = (direction: "left" | "right") => {
    const newIndex = direction === "left" ? sessionIndex + 1 : sessionIndex - 1;
    const session = sessions[newIndex];
    if (!session || !session.id) return;
    setSessionSwitchLoading(true);
    monitorApi
      .getDnsMonitoringData(session.id)
      .then((data: any) => {
        setMonitoringData(data);
        setSessionIndex(newIndex);
      })
      .catch(() => {})
      .finally(() => {
        setSessionSwitchLoading(false);
      });
  };

  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>();
    const list = monitoringData?.super_categories ?? [];
    for (const c of list) map.set(c.id, c.name);
    return map;
  }, [monitoringData]);

  const summary = useMemo(() => {
    if (
      !monitoringData ||
      monitoringData.error ||
      !Array.isArray(monitoringData.domain_data)
    ) {
      return {
        nsdiDeleted: 0,
        ruDeleted: 0,
        globalDeleted: 0,
        deletedTotal: 0,
        totalDomains: 0,
        buckets: { nsdi: 0, nsdiRu: 0, ru: 0, resolved: 0 },
        totalForChart: 0,
      };
    }

    const nsdiDeleted = monitoringData.domain_data.filter((item) =>
      (item.nsdi_dns ?? []).some(
        (x) =>
          !x.resolved &&
          String(x.error || "")
            .toUpperCase()
            .includes("NXDOMAIN"),
      ),
    ).length;

    const ruDeleted = monitoringData.domain_data.filter((item) =>
      (item.russian_dns ?? []).find((x) => x.resolved === false),
    ).length;

    const globalDeleted = monitoringData.domain_data.filter((item) =>
      (item.global_dns ?? []).find((x) => x.resolved === false),
    ).length;

    const deletedTotal = monitoringData.total_deleted_entries_count;

    /* istanbul ignore next */
    const domainList = Array.isArray(monitoringData.domain_data)
      ? monitoringData.domain_data
      : [];

    const totalDomains = toNum(monitoringData.total_tested_domains_count);

    const buckets = { nsdi: 0, nsdiRu: 0, ru: 0, resolved: 0 };

    for (const d of domainList) {
      const nsdiUn =
        d?.nsdi !== "resolved" && d?.nsdi !== "no_data" && d?.nsdi != null;
      const ruNotOk = isNotFullyResolved(d?.russian_dns);

      if (nsdiUn && !ruNotOk) buckets.nsdi += 1;
      else if (nsdiUn && ruNotOk) buckets.nsdiRu += 1;
      else if (!nsdiUn && ruNotOk) buckets.ru += 1;
    }

    const unresolvedSum = buckets.nsdi + buckets.nsdiRu + buckets.ru;
    buckets.resolved = Math.max(0, totalDomains - unresolvedSum);

    const totalForChart = totalDomains;

    // istanbul ignore next
    const okDomainsCount =
      monitoringData.ok_domains_count ??
      (monitoringData.ok_domains != null
        ? monitoringData.ok_domains.length
        : 0);

    return {
      nsdiDeleted,
      ruDeleted,
      globalDeleted,
      deletedTotal,
      totalDomains,
      buckets,
      totalForChart,
      okDomainsCount,
    };
  }, [monitoringData]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredDomains = useMemo(() => {
    const list = Array.isArray(monitoringData?.domain_data)
      ? monitoringData.domain_data
      : [];

    const filters = columns.reduce(
      (acc, col) => ({
        ...acc,
        [col.key]: (columnFilters[col.key] != null
          ? columnFilters[col.key]
          : /* istanbul ignore next */ ""
        )
          .trim()
          .toLowerCase(),
      }),
      {},
    );

    const hasAnyFilter = Object.values(filters).some(Boolean);
    if (!hasAnyFilter) return list;

    const filterHandlers = {
      domain: (d, v) =>
        !String(
          d?.domain_name != null
            ? d.domain_name
            : /* istanbul ignore next */ "",
        )
          .toLowerCase()
          .includes(v),
      category: (d, v) => {
        const catName =
          categoryNameById.get(d.super_category_id) ??
          (d.category_id != null ? `Unknown (${d.category_id})` : "-");
        return !catName.toLowerCase().includes(v);
      },
      nsdi: (d, v) => !getNsdiStatus(d.nsdi).label.toLowerCase().startsWith(v),
      ru: (d, v) =>
        !getDnsStatus(d.russian_dns).label.toLowerCase().startsWith(v),
      global: (d, v) =>
        !getDnsStatus(d.global_dns).label.toLowerCase().startsWith(v),
    };

    return list.filter((d) => {
      for (const col of columns) {
        const handler = filterHandlers[col.key];
        const val = filters[col.key];
        if (handler && val && handler(d, val)) return false;
      }
      return true;
    });
  }, [monitoringData, columnFilters, categoryNameById, columns]);

  const isSearchMode = Object.values(columnFilters).some((v) => v.trim());

  const totalPages = useMemo(() => {
    if (isSearchMode) return 1;
    const total = filteredDomains.length;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [filteredDomains.length, isSearchMode, PAGE_SIZE]);

  const currentPage = Math.min(Math.max(1, page), totalPages);

  const pagedDomains = useMemo(() => {
    if (isSearchMode) return filteredDomains;

    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredDomains.slice(start, end);
  }, [filteredDomains, isSearchMode, currentPage, PAGE_SIZE]);

  if (!monitoringData) {
    return (
      <div className="main-body bg-cream min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-8 md:py-12">
          <h1 className="f-size-2 fw-700 color-green mb-6">Монитор DNS</h1>
          <CheckDomain />
          <div className="mb-2 mt-4 flex items-center gap-4">
            <DateSwitcher
              displayDate={getCurrentDateString()}
              canGoLeft={false}
              canGoRight={false}
              onLeft={loadingDateSwitcherNoop}
              onRight={loadingDateSwitcherNoop}
              isLoading
            />
          </div>
          <div>Загрузка...</div>
        </div>
      </div>
    );
  }

  if (monitoringData.error) {
    return (
      <div className="main-body bg-cream min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-8 md:py-12">
          <h1 className="f-size-2 fw-700 color-green mb-6">Мониторинг DNS</h1>
          <div className="monitoring-error-box">
            Данные временно недоступны. Пожалуйста, зайдите позже.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-body bg-cream min-h-screen">
      <Header />
      <div className="container mx-auto px-4 py-8 md:py-12">
        <h1 className="f-size-2 fw-700 color-green mb-6">Монитор DNS</h1>
        <div className="monitoring-header-row mb-8">
          <div className="monitoring-description">
            <p className="mb-1">
              Мы составили список, в который включены популярные сайты, доступ к
              которым может быть ограничен в России. Сервис «Монитор DNS» делает
              запрос по каждому домену из списка к DNS-провайдеру{" "}
              <HeaderHelpIcon
                title="DNS"
                text={<HelperTextDns />}
                showTooltip={showTooltip}
                moveTooltip={moveTooltip}
                hideTooltip={hideTooltip}
              />
              , обрабатывает ответ и записывает результат: если есть ошибка
              «DNS_PROBE_FINISHED_NXDOMAIN», то значение в табличке будет
              NXDOMAIN. Это означает, что такого домена в записях DNS не
              существует. NXDOMAIN расшифровывается, как «non-existing domain» и
              это значит, что в справочнике попросту отсутствуют IP-адреса этих
              ресурсов. Для прочих ошибок, в таблице выводится «unresolved» Мы
              пока добавили для первого этапа по 5 российских и мировых DNS, и
              выбрали из большого списка несколько, чтобы учесть разные регионы
              России. Дальше будем постепенно добавлять новые!
            </p>
          </div>

          <div className="monitoring-updated-wrap">
            <div className="monitoring-updated-at">
              Обновлено:{" "}
              <span>
                {formatUpdatedAt(
                  monitoringData.date,
                  RECENT_UPDATE_THRESHOLD_HOURS,
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="monitoring-summary-grid mb-8">
          <div className="monitoring-card">
            <div className="monitoring-card-title">
              Домены, удалённые у глобальных провайдеров
            </div>
            <div className="monitoring-card-value">{summary.globalDeleted}</div>
          </div>

          <div className="monitoring-card">
            <div className="monitoring-card-title">
              Домены удалённые у РФ провайдеров
            </div>
            <div className="monitoring-card-value">{summary.ruDeleted}</div>
          </div>

          <div className="monitoring-card">
            <div className="monitoring-card-title">Домены удалённые у НСДИ</div>
            <div className="monitoring-card-value">{summary.nsdiDeleted}</div>
          </div>

          <div className="monitoring-card">
            <div className="monitoring-card-title">Доменов в нашем списке</div>
            <div className="monitoring-card-value">
              {(monitoringData.ok_domains_count ?? 0) +
                (monitoringData.nxdomain_domains_count ?? 0)}
            </div>
          </div>
        </div>

        {/* <div className="monitoring-chart-card mb-12">
          <div className="monitoring-card-title">Диаграмма</div>

          {(() => {
            const total = summary.totalForChart;
            const segments = [
              {
                key: "НСДИ недоступно",
                label: "НСДИ недоступно",
                value: summary.nsdiDeleted,
                color: "#c40619",
              },
              {
                key: "НСДИ доступно",
                label: "НСДИ доступно",
                value:
                  monitoringData.total_tested_domains_count -
                  summary.nsdiDeleted,
                color: "#5e7638",
              },
            ];

            return (
              <PieChart
                total={total}
                segments={segments}
                hoveredKey={tooltip?.kind === "chart" ? tooltip?.key : null}
                onEnter={(e, seg) => {
                  const percent =
                    total > 0 ? (toNum(seg.value) / total) * 100 : 0;

                  showTooltip(e, {
                    kind: "chart",
                    key: seg.key,
                    title: seg.label,
                    value: `${toNum(seg.value)} / ${total} (${percent.toFixed(
                      1
                    )}%)`,
                  });
                }}
                onMove={moveTooltip}
                onLeave={hideTooltip}
              />
            );
          })()}

          <div className="monitoring-chart-legend">
            <span>
              <i className="monitoring-dot monitoring-dot-nsdi" />
              НСДИ недоступно
            </span>
            <span>
              <i className="monitoring-dot monitoring-dot-resolved" />
              НСДИ доступно
            </span>
          </div>
        </div> */}

        <CheckDomain />

        <div className="mb-2 mt-4 flex items-center gap-4">
          <DateSwitcher
            displayDate={formatDateForDisplay(monitoringData.date)}
            canGoLeft={canGoLeft}
            canGoRight={canGoRight}
            onLeft={() => handleSessionSwitch("left")}
            onRight={() => handleSessionSwitch("right")}
            isLoading={sessionSwitchLoading}
          />
          {sessionSwitchLoading && (
            <span className="date-switcher-loading">Загрузка...</span>
          )}
        </div>

        <div className="mb-1">
          Таблица отображает данные последнего результата проверки
        </div>

        {isSearchMode && (
          <div className="monitoring-search-hint mb-1">
            Найдено: {filteredDomains.length}
          </div>
        )}
        {!isSearchMode && totalPages > 1 && (
          <div className="monitoring-pagination">
            <button
              className="monitoring-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← Назад
            </button>

            <div className="monitoring-page-info">
              Страница <b>{currentPage}</b> из <b>{totalPages}</b>{" "}
              <span className="monitoring-page-range">
                (показано{" "}
                {Math.min(
                  (currentPage - 1) * PAGE_SIZE + 1,
                  filteredDomains.length,
                )}
                –{Math.min(currentPage * PAGE_SIZE, filteredDomains.length)} из{" "}
                {filteredDomains.length})
              </span>
            </div>

            <button
              className="monitoring-page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Вперёд →
            </button>
          </div>
        )}

        <div className="monitoring-table-wrapper">
          <table className="monitoring-table">
            <thead>
              <MonitoringTableHead
                columns={columns}
                showTooltip={showTooltip}
                moveTooltip={moveTooltip}
                hideTooltip={hideTooltip}
                HeaderHelpIcon={HeaderHelpIcon}
              />
              <MonitoringFilterRow
                columnFilters={columnFilters}
                setColumnFilter={setColumnFilter}
                filterKeys={columns.map((c) => c.key)}
              />
            </thead>

            <tbody>
              {pagedDomains.map((domain) => {
                const nsdi = getNsdiStatus(domain.nsdi);
                const russian = getDnsStatus(domain.russian_dns);
                const global = getDnsStatus(domain.global_dns);
                const categoryName =
                  categoryNameById.get(domain.super_category_id) ??
                  (domain.category_id != null
                    ? `Unknown (${domain.category_id})`
                    : "-");

                const cellRenderers = {
                  domain: () => domain.domain_name,
                  category: () => categoryName,
                  nsdi: () => (
                    <span
                      className={`status-pill status-${nsdi.variant} status-has-tooltip`}
                      onMouseEnter={(e) =>
                        showTooltip(e, buildSimpleTooltip("НСДИ", nsdi.label))
                      }
                      onMouseMove={moveTooltip}
                      onMouseLeave={hideTooltip}
                    >
                      {nsdi.label}
                    </span>
                  ),
                  ru: () => (
                    <span
                      className={`status-pill status-${russian.variant} status-has-tooltip`}
                      onMouseEnter={(e) => {
                        setScrollHover(true);
                        showTooltip(
                          e,
                          buildDnsTooltip("RU", domain.russian_dns),
                        );
                      }}
                      onMouseMove={moveTooltip}
                      onMouseLeave={() => {
                        setScrollHover(false);
                        hideTooltip();
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        setScrollHover(true);
                        showTooltip(
                          e,
                          buildDnsTooltip("RU", domain.russian_dns),
                        );
                      }}
                    >
                      {russian.label}
                    </span>
                  ),
                  global: () => (
                    <span
                      className={`status-pill status-${global.variant} status-has-tooltip`}
                      onMouseEnter={(e) => {
                        setScrollHover(true);
                        showTooltip(
                          e,
                          buildDnsTooltip("Global", domain.global_dns),
                        );
                      }}
                      onMouseMove={moveTooltip}
                      onMouseLeave={() => {
                        setScrollHover(false);
                        hideTooltip();
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        setScrollHover(true);
                        showTooltip(
                          e,
                          buildDnsTooltip("Global", domain.global_dns),
                        );
                      }}
                    >
                      {global.label}
                    </span>
                  ),
                };

                return (
                  <tr key={domain.domain_name}>
                    {columns.map((col: any) => (
                      <td key={col.key}>
                        {cellRenderers[col.key]
                          ? cellRenderers[col.key]()
                          : /* istanbul ignore next */ null}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {!isSearchMode && (
                <tr className="monitoring-total-row">
                  {columns.map((col: any, idx: number) => (
                    <td key={col.key}>
                      {idx === 0 ? (
                        <span>Оставшиеся {summary.okDomainsCount}</span>
                      ) : idx === 1 ? (
                        <span>разное</span>
                      ) : (
                        <span className="status-pill status-resolved">
                          resolved
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!isSearchMode && totalPages > 1 && (
          <div className="monitoring-pagination">
            <button
              className="monitoring-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← Назад
            </button>

            <div className="monitoring-page-info">
              Страница <b>{currentPage}</b> из <b>{totalPages}</b>{" "}
              <span className="monitoring-page-range">
                (показано{" "}
                {Math.min(
                  (currentPage - 1) * PAGE_SIZE + 1,
                  filteredDomains.length,
                )}
                –{Math.min(currentPage * PAGE_SIZE, filteredDomains.length)} из{" "}
                {filteredDomains.length})
              </span>
            </div>

            <button
              className="monitoring-page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Вперёд →
            </button>
          </div>
        )}
      </div>
      <HoverTooltip
        data={tooltip}
        scrollRef={tooltipScrollRef}
        ref={tooltipRootRef}
      />
    </div>
  );
};

export default MonitoringPage;
